const { getElements } = window.BAO.Utils

/**
 * SystemBuilder - Main orchestrator component
 *
 * Coordinates child components and manages:
 * - State initialization via SystemBuilderState
 * - Cart operations (add to cart, update button)
 * - DOM queries for product elements
 * - Event coordination between child components
 *
 * Child components handle:
 * - Navigation (subsystem/layer switching)
 * - Product selection and management
 * - Info drawer
 * - URL state management
 *
 * @class
 * @extends {window.BAO.CustomElement}
 */
class SystemBuilder extends window.BAO.CustomElement() {
  constructor () {
    super()

    if (!window.BAO.SystemBuilder) {
      window.BAO.SystemBuilder = {
        state: null,
        core: null,
        isReady: false,
        productCache: new Map(),

        attach (component) {
          component.state = this.state
          component.core = this.core
          component.sharedEls = this.core.els
        },

        getState () {
          return this.state
        },

        getAllProductsForLayer (subSystemIndex, layerIndex) {
          return (
            this.core?.getAllProductsForLayer(subSystemIndex, layerIndex) ?? []
          )
        },
      }
    }
  }

  connectedCallback () {
    super.connectedCallback()

    const data = JSON.parse(this.els.data.element.textContent)
    this.state = new window.BAO.SystemBuilderState(data)

    window.BAO.SystemBuilder.state = this.state
    window.BAO.SystemBuilder.core = this

    this.#instantiateChildComponents()
  }

  #instantiateChildComponents () {
    const requiredComponents = [
      'system-builder-nav',
      'system-builder-products',
      'system-builder-drawer',
      'system-builder-url',
      'system-builder-tooltips',
    ]

    const checkAndCreate = () => {
      const allRegistered = requiredComponents.every((name) =>
        window.customElements.get(name),
      )

      if (allRegistered) {
        requiredComponents.forEach((name) => {
          this.appendChild(document.createElement(name))
        })

        window.BAO.SystemBuilder.isReady = true

        if (window.BAO.eventBus) {
          window.BAO.eventBus.dispatchEvent(
            window.BAO.EVENTS.SYSTEM_BUILDER.READY,
            { timestamp: performance.now() },
          )
        }
      } else {
        requestAnimationFrame(checkAndCreate)
      }
    }

    checkAndCreate()
  }

  setupListeners () {
    super.setupListeners()

    this.handleAddToCart = this.handleAddToCart.bind(this)

    this.els?.['add-to-cart']?.elements.forEach((addToCartEl) => {
      this.listeners.add(addToCartEl, 'click', this.handleAddToCart)
    })

    this.#setupEventListeners()
  }

  /**
   * Setup event listeners for child component coordination
   *
   * @private
   */
  #setupEventListeners () {
    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.STATE_CHANGED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.STATE_CHANGED,
        this.#onStateChanged.bind(this),
      )
    }

    if (
      window.BAO.eventBus &&
      window.BAO.EVENTS?.SYSTEM_BUILDER?.SUBSYSTEM_CHANGED
    ) {
      window.BAO.eventBus.addEventListener(
        window.BAO.EVENTS.SYSTEM_BUILDER.SUBSYSTEM_CHANGED,
        this.#onSubsystemChanged.bind(this),
      )
    }
  }

  #onStateChanged (event) {
    this.handleUpdateButton()
  }

  #onSubsystemChanged (event) {
    this.handleUpdateButton()
  }

  /**
   * Add all selected products to cart
   *
   * Collects all selected products from the current subsystem
   * and adds them to the cart via window.BAO.Cart
   *
   * @async
   */
  async handleAddToCart () {
    const subSystem = this.state.getSubSystem(this.state.subSystemIndex)
    if (!subSystem) return

    const items = subSystem.layers.flatMap((layer) =>
      layer.groups
        .filter((group) => group.productSelected?.variantId)
        .map((group) => ({
          id: group.productSelected.variantId,
          quantity: 1,
        })),
    )

    await window.BAO.Cart.addItem(items)
  }

  /**
   * Update add to cart button state and price
   *
   * Calculates total price from all selected products in current subsystem
   * Updates button disabled state and price display
   */
  handleUpdateButton () {
    let total = 0

    const subSystem = this.state.getSubSystem(this.state.subSystemIndex)
    if (subSystem) {
      subSystem.layers.forEach((layer) => {
        layer.groups.forEach((group) => {
          if (group.productSelected && group.productSelected.price) {
            total += group.productSelected.price
          }
        })
      })
    }

    const priceElements = this.els.price?.elements
    priceElements?.forEach((priceElement) => {
      priceElement.textContent = window.BAO.Utils.formatMoney(total)
    })

    const addToCartButtons = this.els['add-to-cart']?.elements
    addToCartButtons?.forEach((addToCartElement) => {
      addToCartElement.disabled = total === 0
    })
  }

  /**
   * Get all product elements for a specific layer
   *
   * Queries both desktop and mobile product containers
   * Returns combined array of product elements
   * Results are cached for performance
   *
   * @param {number} subSystemIndex - Index of the subsystem
   * @param {number} layerIndex - Index of the layer within the subsystem
   * @returns {HTMLElement[]} Array of product elements
   */
  getAllProductsForLayer (subSystemIndex, layerIndex) {
    const cacheKey = `${subSystemIndex}-${layerIndex}`

    if (window.BAO.SystemBuilder.productCache.has(cacheKey)) {
      return window.BAO.SystemBuilder.productCache.get(cacheKey)
    }

    let desktopProducts = []
    let mobileProducts = []

    // Get desktop products
    this.els['subsystem-products']?.elements.forEach((subsystemEl) => {
      const subsystemIdx = parseInt(subsystemEl.dataset.systemSubsystemIndex)
      if (subsystemIdx === subSystemIndex) {
        const productsContainers = subsystemEl.querySelectorAll(
          this.els.products.selector,
        )
        const container = productsContainers[layerIndex]
        if (container) {
          desktopProducts = getElements(this.els.product.selector, {
            context: container,
          })
        }
      }
    })

    // Get mobile products from drawer
    this.els['drawer-content']?.elements.forEach((drawerEl) => {
      const drawerSubsystem = parseInt(
        drawerEl.closest('[data-system-subsystem-index]')?.dataset
          .systemSubsystemIndex,
      )
      if (drawerSubsystem === subSystemIndex) {
        const drawerContainer = drawerEl.parentElement
        const allDrawerContents = drawerContainer?.querySelectorAll(
          this.els['drawer-content'].selector,
        )
        const relativeIndex = allDrawerContents
          ? Array.from(allDrawerContents).indexOf(drawerEl)
          : -1
        if (relativeIndex === layerIndex) {
          mobileProducts = getElements(this.els.product.selector, {
            context: drawerEl,
          })
        }
      }
    })

    const products = [...desktopProducts, ...mobileProducts]
    window.BAO.SystemBuilder.productCache.set(cacheKey, products)

    return products
  }
}

if (!customElements.get('system-builder')) {
  customElements.define('system-builder', SystemBuilder)
}
